import React from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import {
  Users, UserCheck, AlertTriangle, CalendarClock,
  ShoppingBag, IndianRupee, TrendingUp, UserCog, Building2,
  Wrench, ArrowDownToLine, ArrowUpFromLine, Hammer, Package,
  ClipboardCheck, ListTodo, Wallet, Receipt, Sparkles,
  MessageCircle, Phone, ExternalLink,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* ────────────────────────────────────────────────────────────
   substitutePlaceholders — client-side {{key}} replacement.
   Unknown placeholders are left as-is so missing data doesn't break the body.
   ──────────────────────────────────────────────────────────── */
const substitutePlaceholders = (body, ctx) => {
  if (!body) return '';
  return body.replace(/\{\{(\w+)\}\}/g, (m, key) => {
    const v = ctx[key];
    return (v === undefined || v === null || v === '') ? m : String(v);
  });
};

const stripHtml = (html) => {
  if (!html) return '';
  // Lightweight HTML strip — converts <br> / <p> to newlines, removes other tags
  return html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .trim();
};

/* ────────────────────────────────────────────────────────────
   ContactActions — tiny WhatsApp (template-picker popover) + Call icons
   that appear next to a Mobile No cell. Used by both the Sales · Customer
   Balance table and the Service · Reconciliation Outstanding table.

   Props:
     mobile (string)    : the phone number, plaintext
     templateSource (string): 'sales' | 'service' — which template pool to fetch
     ctx (object)       : placeholder context for substitution on click
   ──────────────────────────────────────────────────────────── */
function ContactActions({ mobile, templateSource, ctx }) {
  const [open, setOpen] = React.useState(false);
  const [templates, setTemplates] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [pos, setPos] = React.useState({ top: 0, left: 0 });
  const triggerRef = React.useRef(null);
  const popRef = React.useRef(null);

  const computePosition = React.useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const popWidth = 288; // w-72
    const popMaxHeight = 320;
    // Default: drop down + right-aligned to the trigger
    let top = r.bottom + 4;
    let left = r.right - popWidth;
    // If popover would overflow the viewport bottom → open upward
    if (top + popMaxHeight > window.innerHeight - 8) {
      top = Math.max(8, r.top - popMaxHeight - 4);
    }
    // If popover would overflow the viewport left edge → clamp
    if (left < 8) left = 8;
    setPos({ top, left });
  }, []);

  React.useEffect(() => {
    if (!open || templates !== null) return;
    setLoading(true);
    const url = templateSource === 'service'
      ? `${API}/service/templates?channel=whatsapp`
      : `${API}/templates?template_type=whatsapp`;
    axios.get(url)
      .then((r) => {
        const items = Array.isArray(r.data) ? r.data : [];
        const active = items.filter((t) => t.is_active !== false);
        // Sales templates may have a category field; filter to 'sales' if present
        const filtered = templateSource === 'sales'
          ? active.filter((t) => !t.category || t.category === 'sales' || t.category === 'booking')
          : active;
        setTemplates(filtered);
      })
      .catch(() => setTemplates([]))
      .finally(() => setLoading(false));
  }, [open, templates, templateSource]);

  React.useEffect(() => {
    if (!open) return;
    computePosition();
    const handler = (e) => {
      if (popRef.current && popRef.current.contains(e.target)) return;
      if (triggerRef.current && triggerRef.current.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    window.addEventListener('scroll', computePosition, true);
    window.addEventListener('resize', computePosition);
    return () => {
      document.removeEventListener('mousedown', handler);
      window.removeEventListener('scroll', computePosition, true);
      window.removeEventListener('resize', computePosition);
    };
  }, [open, computePosition]);

  if (!mobile) return <span className="text-slate-400">—</span>;

  const cleanMobile = String(mobile).replace(/\D/g, '');
  const numForLink = cleanMobile.length === 10 ? `91${cleanMobile}` : cleanMobile;

  const handlePickTemplate = (tpl) => {
    const body = tpl.channel === 'whatsapp' || tpl.type === 'whatsapp'
      ? (tpl.body || '')
      : stripHtml(tpl.body || '');
    const substituted = substitutePlaceholders(stripHtml(body), ctx);
    const url = `https://wa.me/${numForLink}?text=${encodeURIComponent(substituted)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
    setOpen(false);
  };

  const handlePickEmpty = () => {
    const url = `https://wa.me/${numForLink}`;
    window.open(url, '_blank', 'noopener,noreferrer');
    setOpen(false);
  };

  const settingsLink = templateSource === 'service' ? '/service/settings/templates' : '/sales/settings/templates';

  const popover = open ? createPortal(
    <div
      ref={popRef}
      style={{ position: 'fixed', top: pos.top, left: pos.left, width: 288, zIndex: 100 }}
      className="bg-white border border-slate-200 rounded-xl shadow-2xl p-2"
      data-testid="whatsapp-template-picker"
    >
      <div className="px-2 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500 border-b border-slate-100 mb-1 flex items-center justify-between">
        <span>Pick a WhatsApp template</span>
        <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-600 text-xs">✕</button>
      </div>
      {loading && <div className="px-2 py-3 text-sm text-slate-500">Loading…</div>}
      {!loading && templates && templates.length === 0 && (
        <div className="px-2 py-3">
          <p className="text-sm text-slate-700 mb-2">No active WhatsApp templates yet.</p>
          <a href={settingsLink} className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800 font-medium">
            Create one in {templateSource === 'service' ? 'Service' : 'Sales'} → Templates <ExternalLink className="w-3 h-3" />
          </a>
          <div className="border-t border-slate-100 mt-2 pt-2">
            <button onClick={handlePickEmpty} className="text-xs text-slate-600 hover:text-slate-800">
              Or open WhatsApp without a message →
            </button>
          </div>
        </div>
      )}
      {!loading && templates && templates.length > 0 && (
        <div className="max-h-64 overflow-y-auto">
          {templates.map((t) => (
            <button
              key={t.id}
              onClick={() => handlePickTemplate(t)}
              className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-emerald-50 transition-colors group"
              data-testid={`tpl-${t.id}`}
            >
              <div className="text-sm font-medium text-slate-800 group-hover:text-emerald-800 truncate">{t.name}</div>
              {t.description && <div className="text-[11px] text-slate-500 truncate">{t.description}</div>}
            </button>
          ))}
        </div>
      )}
    </div>,
    document.body,
  ) : null;

  return (
    <span className="inline-flex items-center gap-2">
      <span className="tabular-nums">{mobile}</span>
      <span className="inline-flex items-center gap-1">
        <button
          ref={triggerRef}
          onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
          className="w-6 h-6 rounded-md bg-emerald-50 hover:bg-emerald-100 text-emerald-700 hover:text-emerald-800 inline-flex items-center justify-center transition-colors border border-emerald-200"
          title="Send WhatsApp message (from template)"
          data-testid={`whatsapp-btn-${cleanMobile}`}
        >
          <MessageCircle className="w-3.5 h-3.5" />
        </button>
        <a
          href={`tel:+${numForLink}`}
          onClick={(e) => e.stopPropagation()}
          className="w-6 h-6 rounded-md bg-sky-50 hover:bg-sky-100 text-sky-700 hover:text-sky-800 inline-flex items-center justify-center transition-colors border border-sky-200"
          title="Call this number"
          data-testid={`call-btn-${cleanMobile}`}
        >
          <Phone className="w-3.5 h-3.5" />
        </a>
      </span>
      {popover}
    </span>
  );
}

/* ────────────────────────────────────────────────────────────
   Atomic Tile — small, rich, gradient-tinted KPI card.
   Designed for 2-col mobile, 3-col tablet, 4-col laptop, 5-col desktop.
   ──────────────────────────────────────────────────────────── */

const TONES = {
  indigo:  { ring: 'ring-indigo-200/60',  bar: 'from-indigo-400 to-violet-500',   ico: 'text-indigo-500',  num: 'text-indigo-900' },
  violet:  { ring: 'ring-violet-200/60',  bar: 'from-violet-400 to-fuchsia-500',  ico: 'text-violet-500',  num: 'text-violet-900' },
  emerald: { ring: 'ring-emerald-200/60', bar: 'from-emerald-400 to-teal-500',    ico: 'text-emerald-500', num: 'text-emerald-900' },
  amber:   { ring: 'ring-amber-200/60',   bar: 'from-amber-400 to-orange-500',    ico: 'text-amber-500',   num: 'text-amber-900' },
  rose:    { ring: 'ring-rose-200/60',    bar: 'from-rose-400 to-pink-500',       ico: 'text-rose-500',    num: 'text-rose-900' },
  sky:     { ring: 'ring-sky-200/60',     bar: 'from-sky-400 to-blue-500',        ico: 'text-sky-500',     num: 'text-sky-900' },
  slate:   { ring: 'ring-slate-200/70',   bar: 'from-slate-400 to-slate-500',     ico: 'text-slate-500',   num: 'text-slate-900' },
  cyan:    { ring: 'ring-cyan-200/60',    bar: 'from-cyan-400 to-teal-500',       ico: 'text-cyan-500',    num: 'text-cyan-900' },
};

function Tile({ icon: Icon, label, value, sub, tone = 'indigo', testId }) {
  const t = TONES[tone] || TONES.indigo;
  return (
    <div
      data-testid={testId}
      className={`group relative overflow-hidden rounded-xl bg-white/80 backdrop-blur-sm ring-1 ${t.ring} shadow-[0_2px_8px_rgba(15,23,42,0.04)] hover:shadow-[0_6px_20px_rgba(15,23,42,0.08)] transition-shadow p-3 sm:p-3.5`}
    >
      <div className={`absolute left-0 top-0 h-full w-1 bg-gradient-to-b ${t.bar}`} />
      <div className="flex items-start gap-2.5">
        <div className={`mt-0.5 ${t.ico}`}>
          <Icon size={16} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10.5px] font-medium uppercase tracking-wide text-slate-500 truncate">{label}</div>
          <div className={`mt-0.5 text-[20px] sm:text-[22px] font-bold leading-tight ${t.num} truncate`}>{value}</div>
          {sub && <div className="text-[10.5px] text-slate-500 mt-0.5 truncate">{sub}</div>}
        </div>
      </div>
    </div>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <section className="space-y-2.5">
      <div className="flex items-center gap-2 px-1">
        <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-violet-100 to-indigo-100 ring-1 ring-violet-200/50 flex items-center justify-center">
          <Icon size={13} className="text-violet-600" />
        </div>
        <h3 className="text-[12.5px] font-semibold tracking-tight text-slate-700 uppercase">{title}</h3>
        <div className="flex-1 h-px bg-gradient-to-r from-violet-200/60 via-slate-200/40 to-transparent" />
      </div>
      {children}
    </section>
  );
}

/* ────────────────────────────────────────────────────────────
   Synopsis renderer — clean ≤10-word bullets in a 2-col grid.
   ──────────────────────────────────────────────────────────── */
function SynopsisBullets({ text }) {
  if (!text) return null;
  const lines = String(text)
    .split('\n')
    .map((l) => l.trim())
    .map((l) => l.replace(/^[•·\-*]\s+/, '').replace(/^\d+[.)]\s+/, ''))
    .filter(Boolean);
  if (!lines.length) return null;
  return (
    <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-1.5">
      {lines.map((t, i) => (
        <li key={i} className="flex items-start gap-2 text-[13px] text-slate-800 leading-snug">
          <span className="mt-1.5 inline-block w-1.5 h-1.5 rounded-full bg-violet-500 flex-shrink-0" />
          <span className="line-clamp-2">{t}</span>
        </li>
      ))}
    </ul>
  );
}

/* Format helpers */
const fmtINR = (n) => {
  if (n == null || isNaN(n)) return '₹0';
  const v = Math.round(Number(n));
  return '₹' + new Intl.NumberFormat('en-IN').format(v);
};
const compact = (s, n = 14) => (s && String(s).length > n ? String(s).slice(0, n - 1) + '…' : (s || ''));

/* ────────────────────────────────────────────────────────────
   Compact Top-10 table with "View all" link.
   ──────────────────────────────────────────────────────────── */
function TopTable({ rows, totalCount, columns, viewAllHref, emptyText, testId }) {
  if (!rows || rows.length === 0) {
    return (
      <div className="rounded-xl bg-white/70 ring-1 ring-slate-200/60 p-4 text-center text-slate-400 text-[12.5px]">
        {emptyText || 'Nothing to show'}
      </div>
    );
  }
  const top = rows.slice(0, 10);
  return (
    <div className="rounded-xl bg-white/85 backdrop-blur-sm ring-1 ring-slate-200/60 overflow-hidden" data-testid={testId}>
      <div className="overflow-x-auto">
        <table className="w-full text-[12px]">
          <thead className="bg-gradient-to-r from-slate-50 to-white">
            <tr>
              {columns.map((c) => (
                <th key={c.key} className={`px-3 py-2 text-left font-semibold text-slate-600 ${c.align === 'right' ? 'text-right' : ''}`}>
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {top.map((r, i) => (
              <tr key={i} className={`border-t border-slate-100 ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50/40'}`}>
                {columns.map((c) => (
                  <td key={c.key} className={`px-3 py-2 ${c.align === 'right' ? 'text-right tabular-nums font-mono text-slate-800' : 'text-slate-700'}`}>
                    {c.format ? c.format(r[c.key], r) : r[c.key] ?? '—'}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {totalCount > 10 && viewAllHref && (
        <div className="px-3 py-2 border-t border-slate-100 bg-slate-50/60 text-right">
          <a href={viewAllHref} className="text-[11.5px] font-medium text-violet-600 hover:text-violet-800">
            View all {totalCount} →
          </a>
        </div>
      )}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────
   Main component — Newspaper-style 4-column AI Assistant
   ──────────────────────────────────────────────────────────── */
const WINDOWS = [
  { id: 'today',   label: "Today" },
  { id: 'mtd',     label: 'MTD' },
  { id: 'cal_ytd', label: 'YTD (Cal.)' },
  { id: 'fy_ytd',  label: 'YTD (FY)' },
];

function WindowToggle({ value, onChange }) {
  return (
    <div className="inline-flex p-1 bg-slate-100 rounded-xl ring-1 ring-slate-200" data-testid="ai-window-toggle">
      {WINDOWS.map((w) => (
        <button
          key={w.id}
          onClick={() => onChange(w.id)}
          data-testid={`ai-window-${w.id}`}
          className={`px-3 py-1.5 text-[12px] font-medium rounded-lg transition-all ${
            value === w.id
              ? 'bg-white text-violet-700 shadow-sm ring-1 ring-violet-200'
              : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          {w.label}
        </button>
      ))}
    </div>
  );
}

/* Breakdown tile — renders a list inside one card (used for Sales Reps / Financiers). */
function BreakdownTile({ icon: Icon, label, items, testId, tone = 'violet' }) {
  const toneText = tone === 'emerald' ? 'text-emerald-600' : 'text-violet-600';
  return (
    <div className="rounded-xl bg-white ring-1 ring-slate-200/70 p-3 shadow-sm" data-testid={testId}>
      <div className="flex items-center gap-2 mb-2">
        {Icon && <Icon size={13} className={toneText} />}
        <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</div>
      </div>
      {items && items.length ? (
        <ul className="space-y-1">
          {items.slice(0, 5).map((it) => (
            <li key={it.name} className="flex items-center justify-between text-[12.5px]">
              <span className="text-slate-700 truncate mr-2">{compact(it.name, 18)}</span>
              <span className="tabular-nums font-mono text-slate-900 font-semibold">{it.count}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-[12px] text-slate-400 italic">No data</p>
      )}
    </div>
  );
}

export default function DailyAnalyticsContent({ data }) {
  const [win, setWin] = React.useState('today');
  if (!data) return null;
  const {
    ai = {}, customer_balance = {}, reconciliation = {}, ai_synopsis,
  } = data;
  const L = ai.leads   || {};
  const S = ai.sales   || {};
  const SV = ai.service || {};
  const T = ai.tasks   || {};
  const v = (g) => (g && typeof g === 'object' ? (g[win] ?? 0) : 0);
  const STATUS_LABELS = [
    ['delivered', 'Delivered'],
    ['invoice', 'Invoice'],
    ['wip', 'WIP'],
    ['in_queue', 'In Queue'],
    ['test_drive', 'Test Drive'],
    ['warranty', 'Warranty'],
    ['claim_approved', 'Claim Approved'],
    ['claim_rejected', 'Claim Rejected'],
    ['advance', 'Advance'],
  ];

  return (
    <div className="space-y-5" data-testid="ai-metrics-board">
      {/* Header: synopsis (optional) + time-window toggle */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-violet-500 to-indigo-500 flex items-center justify-center text-white shadow-sm">
            <Sparkles size={13} />
          </div>
          <h2 className="text-[13px] font-semibold uppercase tracking-wider text-violet-700">AI Assistant · At a glance</h2>
        </div>
        <WindowToggle value={win} onChange={setWin} />
      </div>

      {ai_synopsis && (
        <div className="rounded-2xl bg-gradient-to-br from-violet-50/80 via-white to-indigo-50/60 ring-1 ring-violet-200/50 backdrop-blur-sm p-4 sm:p-5 shadow-[0_4px_14px_rgba(99,102,241,0.06)]">
          <SynopsisBullets text={ai_synopsis} />
        </div>
      )}

      {/* ─── Newspaper-style 4-column grid: Leads · Sales · Service · Tasks ─── */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="ai-newspaper-grid">

        {/* COLUMN 1 — LEADS */}
        <Section title="Leads" icon={Users}>
          <div className="grid grid-cols-2 gap-2.5">
            <Tile icon={Users}         label="New Leads"       value={v(L.new)}       tone="indigo" testId="tile-ai-leads-new" />
            <Tile icon={UserCheck}     label="Follow-up"       value={v(L.follow_up)} tone="sky"    testId="tile-ai-leads-followup" />
            <Tile icon={AlertTriangle} label="Hot Leads"       value={v(L.hot)}       tone="rose"   testId="tile-ai-leads-hot" />
            <Tile icon={AlertTriangle} label="Lost Leads"      value={v(L.lost)}      tone="amber"  testId="tile-ai-leads-lost" />
          </div>
        </Section>

        {/* COLUMN 2 — SALES */}
        <Section title="Sales" icon={ShoppingBag}>
          <div className="grid grid-cols-2 gap-2.5">
            <Tile icon={TrendingUp}    label="Total Invoiced (DMS)" value={v(S.invoiced)}        tone="emerald" testId="tile-ai-sales-invoiced" />
            <Tile icon={ShoppingBag}   label="Bookings Taken"        value={v(S.bookings)}        tone="violet"  testId="tile-ai-sales-bookings" />
            <Tile icon={AlertTriangle} label="Finance Pending"       value={v(S.finance_pending)} tone="amber"   testId="tile-ai-sales-finance" />
          </div>
          <div className="mt-2.5 grid grid-cols-1 gap-2.5">
            <BreakdownTile icon={UserCog}   label="Sales Reps Wise Bookings" items={(S.sales_reps  || {})[win]} testId="tile-ai-sales-reps"      tone="violet" />
            <BreakdownTile icon={Building2} label="Financier Wise Bookings"  items={(S.financiers  || {})[win]} testId="tile-ai-sales-financiers" tone="emerald" />
          </div>
        </Section>

        {/* COLUMN 3 — SERVICE */}
        <Section title="Service" icon={Wrench}>
          <div className="grid grid-cols-2 gap-2.5">
            <Tile icon={ArrowDownToLine} label="Vehicle Inwards (excl PDI)" value={v(SV.inwards_excl_pdi)} tone="cyan" testId="tile-ai-svc-inwards" />
            {STATUS_LABELS.map(([key, label]) => (
              <Tile
                key={key}
                icon={Hammer}
                label={label}
                value={v((SV.by_status || {})[key])}
                tone={
                  key === 'delivered' ? 'emerald' :
                  key === 'wip' || key === 'in_queue' ? 'amber' :
                  key === 'claim_rejected' ? 'rose' : 'sky'
                }
                testId={`tile-ai-svc-${key}`}
              />
            ))}
          </div>
        </Section>

        {/* COLUMN 4 — TASKS */}
        <Section title="Tasks" icon={ClipboardCheck}>
          <div className="grid grid-cols-2 gap-2.5">
            <Tile icon={ListTodo}       label="New Task Created" value={v(T.new)}         tone="indigo"  testId="tile-ai-tasks-new" />
            <Tile icon={AlertTriangle}  label="Pending Task"     value={v(T.pending)}     tone="rose"    testId="tile-ai-tasks-pending" />
            <Tile icon={ClipboardCheck} label="Completed Task"   value={v(T.completed)}   tone="emerald" testId="tile-ai-tasks-completed" />
            <Tile icon={CalendarClock}  label="Rescheduled Task" value={v(T.rescheduled)} tone="amber"   testId="tile-ai-tasks-rescheduled" />
          </div>
        </Section>
      </div>

      {/* ─── Bottom tables in columnar layout ─── */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {/* Customer Balance — Sales */}
        <Section title="Sales · Customer Balance" icon={Wallet}>
          <TopTable
            rows={(customer_balance.rows || []).map((r, i) => ({ ...r, sno: i + 1 }))}
            totalCount={customer_balance.rows_count || 0}
            viewAllHref="/sales/bookings"
            emptyText="No outstanding customer balances"
            testId="table-customer-balance"
            columns={[
              { key: 'sno', label: 'S.No' },
              { key: 'booking_no', label: 'Booking No' },
              { key: 'name', label: 'Customer Name', format: (_v, r) => `${r.first_name || ''} ${r.last_name || ''}`.trim() || r.customer || '—' },
              { key: 'mobile_no_1', label: 'Mobile No', format: (v, row) => (
                <ContactActions
                  mobile={v}
                  templateSource="sales"
                  ctx={{
                    customer_name: row.customer || '',
                    first_name: (row.customer || '').split(' ')[0] || '',
                    last_name: ((row.customer || '').split(' ').slice(1).join(' ')) || '',
                    mobile: v || '',
                    whatsapp: v || '',
                    booking_no: row.booking_no || '',
                    registration_no: row.reg_no || '',
                    sales_rep: row.sales_rep || '',
                    customer_balance: `₹${fmtINR(Math.abs(row.balance || 0))}`,
                    abs_amount: `₹${fmtINR(Math.abs(row.balance || 0))}`,
                  }}
                />
              ) },
              { key: 'balance', label: 'Balance', align: 'right', format: (v) => <span className={Number(v) < 0 ? 'text-rose-600 font-semibold' : ''}>{fmtINR(v)}</span> },
              { key: 'sales_rep', label: 'Sales Rep' },
            ]}
          />
        </Section>

        {/* Outstanding (Service Reconciliation, including negatives) */}
        <Section title="Service · Reconciliation Outstanding" icon={Receipt}>
          <TopTable
            rows={reconciliation.rows || reconciliation.top_outstanding || []}
            totalCount={reconciliation.outstanding_count || 0}
            viewAllHref="/service/reports/reconciliation"
            emptyText="No outstanding service receivables"
            testId="table-outstanding"
            columns={[
              { key: 'job_no', label: 'Job No' },
              { key: 'customer', label: 'Customer' },
              { key: 'reg_no', label: 'Reg No' },
              { key: 'mobile_no_1', label: 'Mobile No', format: (v, row) => (
                <ContactActions
                  mobile={v}
                  templateSource="service"
                  ctx={{
                    party_name: row.customer || '',
                    customer_name: row.customer || '',
                    mobile_no_1: v || '',
                    job_no: row.job_no || '',
                    reg_no: row.reg_no || '',
                    technician: row.technician || '',
                    outstanding_amount: `₹${fmtINR(Math.abs(row.outstanding || 0))}`,
                    abs_amount: `₹${fmtINR(Math.abs(row.outstanding || 0))}`,
                  }}
                />
              ) },
              { key: 'outstanding', label: 'Outstanding', align: 'right', format: (v) => <span className={Number(v) < 0 ? 'text-rose-600 font-semibold' : ''}>{fmtINR(v)}</span> },
            ]}
          />
        </Section>
      </div>

      <div className="text-center text-[10.5px] text-slate-400 pt-2">
        Last updated · {new Date().toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
      </div>
    </div>
  );
}
